import { useState } from 'react'
import { parseAmountToCents } from '@core/money'
import { markPaid, remainingCents, type Payable } from '@core/payments'
import { moveToTrash } from '@core/trash'
import { Button } from '@design/components'
import { LedgerActions } from './LedgerActions'
import { ProofChip } from './ProofChip'
import { RecurringChip } from '@features/recurring/RecurringChip'
import { TrashButton } from '@features/trash/TrashScreen'
import { UndoEntry } from './UndoEntry'

/**
 * Everything you can do to a payment, in the lower third of the screen.
 *
 * Its own file because the card above it is about *reading* a payment — the
 * amount, the account, the code you scan — and this is about acting on one.
 * They change for different reasons, and the card was outgrowing the
 * two-hundred-line rule.
 *
 * Order is deliberate: the thing you came to do is the large button, and every
 * step down the stack is rarer and quieter, ending with the one that throws
 * the payment away.
 */
export function PaymentActions({
  payable,
  userId,
  onPaid,
  onProof,
  setError,
}: {
  payable: Payable
  userId: string
  onPaid: () => void
  onProof: (documentId: string) => void
  setError: (message: string | null) => void
}) {
  const [saving, setSaving] = useState(false)
  const [partial, setPartial] = useState(false)
  const [amountInput, setAmountInput] = useState((remainingCents(payable) / 100).toFixed(2))
  const owed = remainingCents(payable)

  return (
    <>
      {partial && (
        <div className="shrink-0 px-4">
          <label className="block text-sm text-muted mb-1">Hoeveel heb je betaald?</label>
          <input
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
            inputMode="decimal"
            className="w-full min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line text-lg"
          />
        </div>
      )}

    <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line space-y-2">
      <Button
        size="large"
        disabled={saving}
        onClick={async () => {
          setSaving(true)
          setError(null)
          // Same parser as everywhere else. A hand-rolled replace(',', '.')
          // turns "1.289,70" into NaN and, worse, "1.289" into €1,29.
          const parsed = partial ? parseAmountToCents(amountInput) : owed
          const cents = parsed ?? NaN
          if (!Number.isFinite(cents) || cents <= 0) {
            setError('Geef een geldig bedrag')
            setSaving(false)
            return
          }
          try {
            await markPaid({ payable, amountCents: cents, userId })
            onPaid()
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
            setSaving(false)
          }
        }}
      >
        {saving ? 'Bezig…' : partial ? 'Deelbetaling opslaan' : 'Betaald'}
      </Button>

      <button
        onClick={() => setPartial((p) => !p)}
        className="w-full py-2 text-sm text-muted"
      >
        {partial ? 'Toch het volledige bedrag' : 'Ik heb maar een deel betaald'}
      </button>

      {/* Offered, never required — and it stays on the item forever, so
          it is shown whether or not this has been paid yet. */}
      <ProofChip payable={payable} userId={userId} onView={onProof} />

      {/* Only once something has actually been booked — an undo offered on a
          payment nobody has touched is just noise. */}
      {payable.paidCents > 0 && <UndoEntry payable={payable} onDone={onPaid} />}

      <RecurringChip payable={payable} userId={userId} onCreated={onPaid} />

      <LedgerActions payable={payable} userId={userId} onDone={onPaid} />

      {/* Only where nothing has been paid. The database refuses the rest,
          but offering a button that always fails is its own small cruelty. */}
      {payable.paidCents === 0 && (
        <TrashButton
          onConfirm={async () => {
            await moveToTrash('obligation', payable.obligationId)
            onPaid()
          }}
        />
      )}
    </div>
    </>
  )
}
